<template>
  <!-- Loading: skeleton cards matching real card dimensions -->
  <section v-if="loading" class="featured-shelf">
    <div class="shelf-header">
      <h3 class="section-title">
        <span class="section-icon">⭐</span>
        Curated Featured Sailings
      </h3>
      <router-link to="/best-deals" class="shelf-see-all">Explore all deals →</router-link>
    </div>
    <div class="shelf-row">
      <div v-for="n in 3" :key="`featured-skel-${n}`" class="skeleton-card" aria-hidden="true">
        <div class="skeleton-strip shimmer"></div>
        <div class="skeleton-body">
          <div class="skeleton-line shimmer skeleton-line--title"></div>
          <div class="skeleton-line shimmer skeleton-line--pill"></div>
          <div class="skeleton-line shimmer skeleton-line--meta"></div>
          <div class="skeleton-panel shimmer"></div>
          <div class="skeleton-btn shimmer"></div>
        </div>
      </div>
    </div>
  </section>

  <!-- Error: inline retry, never blank the page -->
  <section v-else-if="error" class="featured-shelf">
    <div class="shelf-header">
      <h3 class="section-title">
        <span class="section-icon">⭐</span>
        Curated Featured Sailings
      </h3>
      <router-link to="/best-deals" class="shelf-see-all">Explore all deals →</router-link>
    </div>
    <div class="shelf-error">
      <p>Unable to load featured sailings.</p>
      <button type="button" class="app-retry-btn" @click="fetchFeatured">Try Again</button>
    </div>
  </section>

  <!-- Loaded with featured cruises: horizontal scroll-snap row. Hides when empty. -->
  <section v-else-if="hasDeals" class="featured-shelf">
    <div class="shelf-header">
      <h3 class="section-title">
        <span class="section-icon">⭐</span>
        Curated Featured Sailings
      </h3>
      <router-link to="/best-deals" class="shelf-see-all">Explore all deals →</router-link>
    </div>
    <!-- CP-119: one section-level cue when every priced card shares it -->
    <p v-if="sharedPriceCue" class="price-freshness shelf-freshness">{{ sharedPriceCue }}</p>
    <div class="shelf-row" :class="rowFitClass">
      <div v-for="deal in featured" :key="`featured-${deal.id}`" class="featured-card">
        <CardIllustrationStrip
          :destination="deal.destination"
          :destination-id="deal.destination_id"
          :name="deal.name"
        />
        <span class="featured-badge">FEATURED</span>

        <div class="featured-body">
          <p class="featured-subline">
            <span v-if="deal.ship_name" class="ship-name">{{ deal.ship_name }}</span>
            <span v-if="deal.ship_name && deal.cruise_line" aria-hidden="true"> · </span>
            <span v-if="deal.cruise_line" class="cs-line-pill">{{ deal.cruise_line }}</span>
          </p>
          <h4 class="cs-title-case deal-title">{{ titleCase(deal.name) }}</h4>
          <p class="featured-meta">
            <template v-if="deal.departure_port">From {{ deal.departure_port }}</template>
            <template v-if="deal.departure_port && deal.duration"> · </template>
            <template v-if="deal.duration">{{ nightsLabel(deal.duration) }}</template>
            <template v-if="deal.departure_port || deal.duration"> · </template>
            {{ formatDate(deal.sail_date || deal.departure_date) }}
          </p>
        </div>

        <div v-if="hasDealPrice(deal)" class="featured-pricing">
          <div v-if="deal.price_per_night" class="price-lead">
            <span class="price-night-value">${{ formatPrice(deal.price_per_night) }}</span>
            <span class="price-night-unit">/ night</span>
          </div>
          <div class="price-secondary">
            Total from ${{ formatPrice(deal.current_price) }} per person
            <span v-if="cabinLabel(deal)" class="cabin-type">({{ cabinLabel(deal) }})</span>
          </div>
          <div v-if="priceCue(deal)" class="price-freshness">{{ priceCue(deal) }}</div>
          <div v-if="deal.verified_clean_value === true" class="price-trust">Verified Clean Value</div>
        </div>
        <div v-else class="featured-pricing">
          <div class="price-unavailable">{{ hiddenPriceReason(deal) }}</div>
          <div v-if="staleAgeStamp(deal)" class="price-freshness">{{ staleAgeStamp(deal) }}</div>
        </div>

        <router-link :to="getCruiseUrl(deal.id, deal.sail_date)" class="view-deal-btn" :aria-label="sailingLinkAriaLabel(deal)">
          {{ sailingLinkText(deal) }}
        </router-link>
      </div>
    </div>
  </section>
</template>

<script>
import apiService from '@/services/api'
import {
  hiddenPriceReason,
  listingCabinLabel,
  listingPriceCue,
  sharedListingPriceCue,
  showsListingPrice,
  staleAgeStamp
} from '@/utils/priceFreshness'
import CardIllustrationStrip from '@/components/CardIllustrationStrip.vue'
import { sailingLinkAriaLabel, sailingLinkText } from '@/utils/sailingLinkLabel'

import { formatPriceAmount } from '@/utils/formatMoney'
import { formatPlaceTitle } from '@/utils/formatPlaceTitle'
export default {
  name: 'FeaturedCruiseShelf',
  components: { CardIllustrationStrip },
  data() {
    return {
      featured: [],
      loading: true,
      error: null
    }
  },
  computed: {
    hasDeals() {
      return !this.loading && !this.error && this.featured.length > 0
    },
    /**
     * CP-99: with fewer than 4 cards the fixed-width scroll row leaves a
     * blank run of empty slots. Stretch 1-3 cards to fill the row instead
     * (1-up / 2-up / 3-up); 4+ keeps the scroll-snap rail.
     */
    rowFitClass() {
      const n = this.featured.length
      if (n > 0 && n < 4) return ['shelf-row--fit', `shelf-row--fit-${n}`]
      return []
    },
    /** CP-119: shown once under the header instead of on every card. */
    sharedPriceCue() {
      return sharedListingPriceCue(this.featured)
    }
  },
  mounted() {
    this.fetchFeatured()
  },
  methods: {
    // CP-212: one pattern for every sailing link.
    sailingLinkText,
    sailingLinkAriaLabel,
    async fetchFeatured() {
      this.loading = true
      this.error = null
      try {
        const response = await apiService.getFeaturedCruises({ limit: 10 })
        this.featured = (response.data && response.data.featured) || []
      } catch (e) {
        console.error('Error fetching featured cruises:', e)
        this.error = 'Unable to load featured sailings.'
      } finally {
        this.loading = false
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'TBD'
      const date = new Date(dateString)
      if (Number.isNaN(date.getTime())) return 'TBD'
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    formatPrice(price) {
      return formatPriceAmount(price)
    },
    hasDealPrice(deal) {
      return showsListingPrice(deal)
    },
    cabinLabel(deal) {
      return listingCabinLabel(deal)
    },
    priceCue(deal) {
      // CP-119: the section line already carries this exact label. A card
      // whose label differs keeps its own cue beside the fare.
      const cue = listingPriceCue(deal)
      return cue && cue === this.sharedPriceCue ? '' : cue
    },
    staleAgeStamp,
    hiddenPriceReason,
    getCruiseUrl(cruiseId, sailDate) {
      let url = `/cruise/${cruiseId}`
      if (sailDate) {
        url += `?sail_date=${encodeURIComponent(sailDate)}`
      }
      return url
    },
    titleCase(str) {
      return formatPlaceTitle(str);
    },
    nightsLabel(n) {
      const num = Number(n)
      if (!Number.isFinite(num) || num <= 0) return ''
      const rounded = Math.round(num)
      return rounded === 1 ? '1 night' : `${rounded} nights`
    },
  }
}
</script>

<style scoped>
.featured-shelf {
  padding: var(--space-6) 0;
}

.shelf-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
}

.section-title {
  color: var(--cs-ink);
  margin: 0;
  font-size: 1.25rem;
  font-family: var(--font-heading);
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.section-icon { font-size: 1.4rem; }

.shelf-see-all {
  font-size: var(--text-small);
  font-weight: 600;
  color: var(--cs-signal-teal);
  text-decoration: none;
  white-space: nowrap;
}

.shelf-see-all:hover {
  color: var(--color-primary-hover);
}

.shelf-freshness {
  margin: calc(-1 * var(--space-2)) 0 var(--space-3);
}

/* Horizontal scroll-snap row.
 * CP-119: a column-flow grid with four shared row tracks (strip / body /
 * pricing / CTA). Each card spans all four and adopts them via subgrid, so
 * price panels and CTAs sit on one baseline across the row no matter how
 * long a title or meta line runs. */
.shelf-row {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 300px;
  grid-template-rows: repeat(4, auto);
  column-gap: var(--space-4);
  row-gap: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding-bottom: var(--space-3);
}

.shelf-row::-webkit-scrollbar {
  display: none;
}

.featured-card,
.skeleton-card {
  scroll-snap-align: start;
  grid-row: span 4;
  min-width: 0;
  border-radius: var(--radius-lg);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: var(--cs-card);
  border: 1px solid var(--cs-card-border);
  box-shadow: var(--shadow-card);
}

.skeleton-card {
  min-height: 380px;
}

.featured-card {
  display: grid;
  grid-template-rows: subgrid;
  row-gap: 0;
  position: relative;
  border-top: 3px solid var(--cs-amber);
  transition: transform var(--transition-normal), box-shadow var(--transition-normal), border-color var(--transition-normal);
}

.featured-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-card-hover);
  border-color: var(--cs-signal-teal);
}

.featured-badge {
  position: absolute;
  top: var(--space-3);
  right: var(--space-3);
  background: var(--cs-amber);
  color: #1A2330;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 3px 10px;
  border-radius: var(--radius-pill);
  z-index: 1;
}

.featured-body {
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  flex: 1;
}

.featured-subline {
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  font-size: 13px;
  line-height: 18px;
  color: var(--cs-muted);
}

.ship-name {
  font-weight: 500;
  color: var(--cs-ink);
}

.deal-title {
  margin: 0;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  color: var(--cs-ink);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.featured-meta {
  margin: 0;
  font-size: var(--text-small);
  color: var(--cs-muted);
  line-height: 1.45;
}

.featured-pricing {
  margin: 0 var(--space-4) var(--space-4);
  padding: var(--space-3);
  background: #e8f5ef;
  border-radius: var(--radius-md);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 2px;
}

.price-lead {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.price-night-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--cs-price);
}

.price-night-unit {
  font-size: var(--text-caption);
  color: var(--cs-price);
  font-weight: 600;
}

.price-secondary {
  font-size: var(--text-caption);
  color: var(--cs-muted);
}

.cabin-type {
  font-size: var(--text-caption);
  color: var(--cs-muted);
  font-weight: normal;
  font-style: italic;
}

.price-trust {
  font-size: var(--text-caption);
  font-weight: 700;
  color: var(--cs-price);
  margin-top: var(--space-1);
}

.view-deal-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  /* CP-212: labels are now "See N-night <destination> sailing" and can wrap to two lines in a narrow card.
     Every button reserves the two-line height (2 x 22px + 2 x 12px padding) so cards in a row stay aligned
     and the skeleton matches the loaded card. */
  min-height: var(--card-link-min-height);
  text-wrap: balance;
  margin: 0 var(--space-4) var(--space-4);
  padding: var(--space-3);
  background-color: var(--cs-fill);
  color: white;
  text-align: center;
  border-radius: var(--radius-md);
  text-decoration: none;
  font-weight: 700;
  transition: background-color var(--transition-normal);
  box-sizing: border-box;
}

.view-deal-btn:hover {
  background-color: var(--cs-fill-hover);
}

.shelf-error {
  text-align: center;
  padding: var(--space-5);
  color: var(--color-error);
  background: var(--color-error-bg);
  border-radius: var(--radius-md);
}

.shelf-error p {
  margin: 0;
}

/* Skeleton */
.skeleton-card {
  pointer-events: none;
}

.skeleton-strip {
  height: 88px;
  width: 100%;
  flex-shrink: 0;
}

.skeleton-body {
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  flex: 1;
}

.skeleton-line {
  border-radius: var(--radius-sm);
  height: 14px;
}

.skeleton-line--title { height: 24px; width: 85%; }
.skeleton-line--pill { height: 20px; width: 55%; border-radius: var(--radius-pill); }
.skeleton-line--meta { width: 70%; }

.skeleton-panel {
  height: 88px;
  border-radius: var(--radius-md);
  margin-top: auto;
}

.skeleton-btn {
  height: var(--card-link-min-height);
  border-radius: var(--radius-md);
}

.shimmer {
  background: linear-gradient(
    90deg,
    var(--color-border-light) 0%,
    var(--cs-signal-mist) 40%,
    var(--color-border-light) 80%
  );
  background-size: 300% 100%;
  animation: featured-shimmer 1.4s ease-in-out infinite;
}

@keyframes featured-shimmer {
  0% { background-position: 150% 0; }
  100% { background-position: -150% 0; }
}

/* Dark-mode pricing panel matches existing shelves */
[data-theme="dark"] .featured-pricing {
  background: var(--color-accent-success-bg);
}

/* CP-99: fewer than 4 cards — fill the row with equal columns (1-up,
 * 2-up or 3-up) so no empty ghost slots imply missing deals. Desktop /
 * tablet only; the mobile rule below keeps the swipe rail. */
@media (min-width: 769px) {
  .shelf-row--fit {
    grid-auto-columns: minmax(0, 1fr);
    overflow-x: visible;
    scroll-snap-type: none;
  }
}

@media (max-width: 768px) {
  .shelf-row {
    grid-auto-columns: 270px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .shimmer {
    animation: none;
  }
}

.price-freshness {
  margin-top: var(--space-2);
  font-size: var(--text-caption);
  line-height: 1.35;
  color: var(--color-text-muted);
}

.price-unavailable {
  color: var(--color-text-muted);
  font-style: italic;
  font-size: var(--text-small);
  font-weight: 600;
}
</style>